import { Helmet } from 'react-helmet-async'

const styles = `
  .x-landing *, .x-landing *::before, .x-landing *::after { margin: 0; padding: 0; box-sizing: border-box; }

  .x-landing {
    --bg: #08080a;
    --surface: #111114;
    --border: rgba(255,255,255,0.06);
    --text: #e8e6e1;
    --text-dim: #7a7872;
    --accent: #38bdf8;
    --accent-dim: rgba(56,189,248,0.08);

    background: var(--bg);
    color: var(--text);
    font-family: 'DM Sans', sans-serif;
    min-height: 100vh;
    overflow-x: hidden;
    -webkit-font-smoothing: antialiased;
    position: relative;
  }

  /* Grain overlay */
  .x-landing::before {
    content: '';
    position: fixed;
    inset: 0;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E");
    pointer-events: none;
    z-index: 9999;
  }

  /* Ambient glow */
  .x-glow {
    position: fixed;
    width: 600px;
    height: 600px;
    border-radius: 50%;
    filter: blur(160px);
    opacity: 0.12;
    pointer-events: none;
    z-index: 0;
  }
  .x-glow-1 { top: -200px; left: -100px; background: var(--accent); }
  .x-glow-2 { bottom: -250px; right: -150px; background: #6366f1; opacity: 0.08; }

  /* Layout */
  .x-page {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    padding: 2rem 1.5rem;
  }

  /* Badge */
  .x-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.4rem 1rem 0.4rem 0.65rem;
    background: var(--accent-dim);
    border: 1px solid rgba(56,189,248,0.15);
    border-radius: 100px;
    font-size: 0.78rem;
    font-weight: 500;
    letter-spacing: 0.03em;
    color: var(--accent);
    margin-bottom: 2.5rem;
    opacity: 0;
    animation: x-fadeUp 0.8s 0.2s ease-out forwards;
  }
  .x-badge .x-dot {
    width: 6px; height: 6px;
    background: var(--accent);
    border-radius: 50%;
    animation: x-pulse 2s infinite;
  }

  /* Headline */
  .x-headline {
    font-family: 'Instrument Serif', serif;
    font-weight: 400;
    font-size: clamp(2.8rem, 7vw, 5.5rem);
    line-height: 1.05;
    text-align: center;
    max-width: 720px;
    letter-spacing: -0.02em;
    opacity: 0;
    animation: x-fadeUp 0.8s 0.35s ease-out forwards;
  }
  .x-headline em {
    font-style: italic;
    color: var(--accent);
  }

  /* Subhead */
  .x-subhead {
    font-size: 1.1rem;
    font-weight: 300;
    color: var(--text-dim);
    text-align: center;
    max-width: 440px;
    line-height: 1.6;
    margin-top: 1.5rem;
    opacity: 0;
    animation: x-fadeUp 0.8s 0.5s ease-out forwards;
  }

  /* Demo grid */
  .x-demo-strip {
    display: flex;
    gap: 1rem;
    margin-top: 3rem;
    opacity: 0;
    animation: x-fadeUp 0.8s 0.65s ease-out forwards;
  }
  .x-demo-card {
    width: 160px;
    height: 96px;
    border-radius: 12px;
    border: 1px solid var(--border);
    background: var(--surface);
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    overflow: hidden;
    transition: border-color 0.3s, transform 0.3s;
  }
  .x-demo-card:hover {
    border-color: rgba(56,189,248,0.2);
    transform: translateY(-2px);
  }
  .x-demo-card .x-label {
    font-size: 0.7rem;
    font-weight: 500;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--text-dim);
  }
  .x-demo-card::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(56,189,248,0.03), transparent);
  }

  /* CTA */
  .x-cta-area {
    margin-top: 3rem;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 1rem;
    opacity: 0;
    animation: x-fadeUp 0.8s 0.8s ease-out forwards;
  }

  .x-cta-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.65rem;
    padding: 1rem 2.2rem;
    background: var(--text);
    color: var(--bg);
    font-family: 'DM Sans', sans-serif;
    font-size: 1rem;
    font-weight: 600;
    border: none;
    border-radius: 100px;
    cursor: pointer;
    text-decoration: none;
    transition: transform 0.2s, box-shadow 0.3s;
    letter-spacing: 0.01em;
  }
  .x-cta-btn:not(.x-cta-btn-outline):hover {
    background: #dddbd6;
  }
  .x-cta-btn-outline {
    background: transparent;
    color: var(--text);
    border: 1px solid rgba(255,255,255,0.25);
  }
  .x-cta-btn svg { width: 20px; height: 20px; }

  .x-cta-hint {
    font-size: 0.78rem;
    color: var(--text-dim);
    letter-spacing: 0.02em;
  }

  /* Stats bar */
  .x-stats {
    display: flex;
    gap: 2.5rem;
    margin-top: 4rem;
    padding-top: 2.5rem;
    border-top: 1px solid var(--border);
    opacity: 0;
    animation: x-fadeUp 0.8s 0.95s ease-out forwards;
  }
  .x-stat {
    text-align: center;
  }
  .x-stat-value {
    font-family: 'Instrument Serif', serif;
    font-size: 1.6rem;
    color: var(--text);
    letter-spacing: -0.01em;
  }
  .x-stat-label {
    font-size: 0.72rem;
    color: var(--text-dim);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    margin-top: 0.25rem;
  }

  /* Animations */
  @keyframes x-fadeUp {
    from { opacity: 0; transform: translateY(16px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes x-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.4; }
  }

  /* Responsive */
  @media (max-width: 600px) {
    .x-demo-strip { gap: 0.6rem; }
    .x-demo-card { width: 100px; height: 64px; }
    .x-stats { gap: 1.5rem; }
    .x-cta-btn { padding: 0.85rem 1.8rem; font-size: 0.95rem; }
  }
`

export default function XLandingPage() {
  return (
    <>
      <Helmet>
        <title>Footage — AI Video Generation</title>
        <meta name="description" content="Describe a scene or upload an image, and Footage generates cinematic AI video in minutes. No editing skills required." />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link href="https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:wght@300;400;500;600&display=swap" rel="stylesheet" />
      </Helmet>
      <style>{styles}</style>
      <div className="x-landing">
        <div className="x-glow x-glow-1" />
        <div className="x-glow x-glow-2" />

        <div className="x-page">
          <div className="x-badge">
            <span className="x-dot" />
            AI-Powered Video Generation
          </div>

          <h1 className="x-headline">
            Turn any idea into <em>stunning</em> video
          </h1>

          <p className="x-subhead">
            Describe a scene or upload an image, and Footage generates cinematic AI video in minutes.          </p>

          <div className="x-cta-area">
            <a className="x-cta-btn x-cta-btn-outline" href="/signup">
              Get Started Free
            </a>
            <a className="x-cta-btn" href="https://x.com/FootageAI" target="_blank" rel="noopener noreferrer">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              Follow @FootageAI
            </a>
          </div>

          <div className="x-stats">
            <div className="x-stat">
              <div className="x-stat-value">3 min</div>
              <div className="x-stat-label">Avg. Generation</div>
            </div>
            <div className="x-stat">
              <div className="x-stat-value">1080p</div>
              <div className="x-stat-label">Output Quality</div>
            </div>
            <div className="x-stat">
              <div className="x-stat-value">10,000+</div>
              <div className="x-stat-label">Creators</div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
